<template>
  <div class="mv-checkin-item">
    <div class="mv-checkin-item-title">
      <img src="~/mv/checkin_icon.svg">
      {{ checkin.fromOffice ? 'Created from the office' : checkin.formattedAddress }}
    </div>

    <div class="d-flex font-size-14 mb-3">
      {{ checkin.employee ? checkin.employee.name : 'None' }}
      <span class="text-muted mx-2">•</span>
      {{ checkin.createdAt }}
    </div>

    <div class="d-flex justify-content-between font-size-14">
      <div class="mv-checkin-item-info">
        <p class="text-break">{{ checkin.description }}</p>
        <div class="d-flex mb-2">
          <span class="info-key">Business</span>
          <span class="d-flex align-items-center gap-2 info-value">
            <template v-if="checkin.business.disconnected">
              <i :id="`${checkin.business.id}-${_uid}`" class="color-warning far fa-exclamation-circle" />
              <BTooltip :target="`${checkin.business.id}-${_uid}`" title="Location is disconnected" />
            </template>
            {{ checkin.business.name }}
          </span>
        </div>
        <div class="d-flex mb-2">
          <span class="info-key">Category</span>
          <span class="info-value">{{ checkin.category ? checkin.category.title : '-' }}</span>
        </div>
        <div class="d-flex mb-2">
          <span class="info-key">Customer</span>
          <span class="info-value">{{ checkin.customer }}</span>
        </div>
        <div class="d-flex mb-2">
          <span class="info-key">Status</span>
          <span :class="['info-value font-weight-semi-bold text-capitalize', stateClass(checkin.aasmState)]">
            {{ checkin.aasmState }}
          </span>
        </div>
        <div v-if='checkin.scheduledAt !== null'>
          <div class="d-flex mb-2">
            <span class="info-key">Scheduled for</span>
            <span class="info-value font-weight-semi-bold text-capitalize">
              {{ checkin.scheduledAt }}
            </span>
          </div>
        </div>
        <div v-if='checkin.aasmState === "approved"'>
          <div class="d-flex mb-2">
            <span class="info-key">GBP Post</span>

            <!-- GMB Post Published -->
            <span v-if='gmbPostStatus(checkin) === "published"' class="info-value d-flex align-items-center gap-2 font-weight-semi-bold color-success">
              <i class="far fa-check" />
              Published
              <a class="d-flex align-items-center gap-2" :href="checkin.localPostSearchUrl" target="_blank">
                <i class="far fa-link" />
                Link to post
              </a>
            </span>

            <!-- GMB Post Skipped -->
            <span v-else-if='gmbPostStatus(checkin) === "skipped"' class="d-flex mb-2">
              Skipped
            </span>

            <!-- GMB Post Rejected -->
            <span v-else-if='gmbPostStatus(checkin) === "rejected"' class="info-value d-flex align-items-center gap-2 font-weight-semi-bold color-danger">
              <i class="far fa-exclamation-circle" />
              Rejected
              <a class="d-flex align-items-center gap-2" :href="localPostUrl(checkin)" target="_blank">
                <i class="far fa-link" />
                Link to post
              </a>
            </span>

            <!-- GMB Post Failed -->
            <span v-else-if='gmbPostStatus(checkin) === "failed"' class="info-value d-flex align-items-center gap-2 font-weight-semi-bold color-danger">
              <i class="far fa-exclamation-circle" />
              Failed
              <a v-if='checkin.localPostSearchUrl' class="d-flex align-items-center gap-2" :href="localPostUrl(checkin)" target="_blank">
                <i class="far fa-link" />
                Link to post
              </a>
            </span>

            <span v-else-if='gmbPostStatus(checkin) === "disconnected"' class="info-value">Skipped</span>

            <!-- GMB Post Deleted -->
            <span v-else-if='gmbPostStatus(checkin) === "deleted"' class="info-value d-flex align-items-center gap-2 font-weight-semi-bold color-warning">
              <i class="far fa-exclamation-circle" />
              Deleted
            </span>

            <span v-else class="info-value">Pending</span>
          </div>
          <div v-if='checkin.webhookStatus' class="d-flex mb-2">
            <span class="info-key">WordPress</span>
            <span class="info-value text-capitalize">{{ webhookStatusDescription(checkin.webhookStatus, checkin.webhookDeliveredAt) }}</span>
          </div>
          <div v-if='checkin.zapierWebhookStatus' class="d-flex mb-2">
            <span class="info-key">Webhook</span>
            <span class="info-value text-capitalize">{{ webhookStatusDescription(checkin.zapierWebhookStatus, checkin.zapierWebhookDeliveredAt) }}</span>
          </div>
        </div>
        <div v-if='checkin.publishVideo && checkin.videoData && checkin.videoData.length' class="d-flex mb-2">
          <span class="info-key">Video status</span>
          <div v-if='gmbPostStatus(checkin) !== "skipped"' class="d-flex align-items-center flex-wrap gap-1 info-value">
            <i v-for="({ state }, index) in checkin.videoData" :key="index" :class="videoStateIcon(state)" />
            <span class="text-muted font-size-12">{{ publishedVideoCount }} of {{ checkin.videoData.length }} successfully published to GBP</span>
          </div>
          <span v-else class="info-value">Skipped</span>
        </div>
        <div v-if="checkin.facebookPostingStatus" class="d-flex mb-2">
          <span class="info-key">FB publication</span>
          <span class="d-flex align-items-center gap-2 info-value">
            <template v-if="checkin.facebookPostingError">
              <i :id="`fb-posting-${checkin.business.id}-${_uid}`" class="text-danger far fa-exclamation-circle" />
              <BTooltip :target="`fb-posting-${checkin.business.id}-${_uid}`" :title="checkin.facebookPostingError" />
            </template>
            {{ facebookStatus(checkin.facebookPostingStatus) }}
            <a v-if="facebookPostPublished(checkin) && checkin.facebookPostUrl"
              class="d-flex align-items-center gap-2"
              :href="checkin.facebookPostUrl"
              target="_blank">
              <i class="far fa-link" />
              Link to post
            </a>
          </span>
        </div>
        <div v-if="checkin.instagramPostingStatus" class="d-flex mb-2">
          <span class="info-key">Instagram publication</span>
          <span class="d-flex align-items-center gap-2 info-value">
            <template v-if="checkin.instagramPostingError">
              <i :id="`instagram-posting-${checkin.business.id}-${_uid}`" class="text-danger far fa-exclamation-circle" />
              <BTooltip :target="`instagram-posting-${checkin.business.id}-${_uid}`" :title="checkin.instagramPostingError" />
            </template>
            <template v-if="checkin.instagramPartlyPublished">
              <i :id="`instagram-incomplete-${checkin.business.id}-${_uid}`" class="color-warning far fa-exclamation-circle" />
              <BTooltip :target="`instagram-incomplete-${checkin.business.id}-${_uid}`" title="Failed to post some of the media" />
            </template>
            {{ facebookStatus(checkin.instagramPostingStatus) }}{{ checkin.instagramPartlyPublished ? ' with warnings' : '' }}
            <a v-if="instagramPostPublished(checkin)" class="d-flex align-items-center gap-2" :href="checkin.instagramPostUrl" target="_blank">
              <i class="far fa-link" />
              Link to post
            </a>
          </span>
        </div>

        <div class="d-flex flex-wrap mt-4 gap-2">
          <button v-if="checkin.aasmState === 'pending'"
            @click="approveUrl(checkin.obfuscatedId)"
            :disabled="isRequestSent"
            type="button"
            class="btn btn-xs px-2 btn-primary rounded">
            Approve
          </button>
          <button v-if="checkin.aasmState === 'pending'"
            @click="rejectUrl(checkin.obfuscatedId)"
            :disabled="isRequestSent"
            type="button"
            class="btn btn-xs px-2 btn-danger rounded">
            Reject
          </button>
          <a :href="editUrl(checkin.obfuscatedId)"
            :class="['btn btn-xs px-2 rounded', { disabled: isRequestSent }]">
            <i class="far fa-edit" />
            <span class="ml-2">{{ editButtonTitle(checkin) }}</span>
          </a>
          <button
            @click="destroyCheckin(checkin.obfuscatedId)"
            :disabled="isRequestSent"
            type="button"
            class="btn btn-xs px-2 btn-outline-danger rounded">
            <i class="far fa-trash" />
            <span class='ml-2'>Delete</span>
          </button>
        </div>
      </div>
      <div v-if="mediaData.length" class="mv-checkin-item-images">
        <template v-if="mediaData.length <= 4">
          <template v-for="({ url, preview, type }, index) in mediaData">
            <img v-if="type === 'image'"
              :key="index"
              :src="preview"
              alt=""
              @click="openPreview(mediaData, index)">
            <video v-else :key="index" :src="url" @click="openPreview(mediaData, index)" />
          </template>
        </template>
        <template v-else>
          <template v-for="({ url, preview, type }, index) in mediaData.slice(0, 3)">
            <img v-if="type === 'image'"
              :key="index"
              :src="preview"
              alt=""
              @click="openPreview(mediaData, index)">
            <video v-else :key="index" :src="url" @click="openPreview(mediaData, index)" />
          </template>
        </template>
        <div v-if="mediaData.length > 4" class="mv-checkin-item-images-more" @click="openPreview(mediaData, 3)">
          +{{ mediaData.length - 3 }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { DEFAULT_ERROR_MESSAGE } from '../../../common/constants'
import { runSwal } from '../../../common/delete_with_swal'
import {
  BASE_URL, BASE_AJAX_URL, FB_STATUSES, handleError
} from './helpers'

export default {
  props: {
    checkin: { type: Object, required: true }
  },
  data() {
    return {
      isRequestSent: false
    }
  },
  computed: {
    publishedVideoCount() {
      return this.checkin.videoData && this.checkin.videoData.filter(({ state }) => state === 'live').length
    },
    mediaData() {
      const media = [...this.checkin.images, ...this.checkin.videoUrls]
      return media.map((m) => ({
        url: typeof m === 'string' ? m : m.url,
        preview: m.preview,
        type: typeof m === 'string' ? 'video' : 'image'
      }))
    }
  },
  methods: {
    stateClass(aasmState) {
      if (aasmState === 'approved') return 'color-success'
      if (aasmState === 'rejected') return 'color-danger '

      return ''
    },
    editUrl(id) {
      return `${BASE_URL}${id}/edit`
    },
    editButtonTitle(checkin) {
      if (checkin.aasmState === 'pending') return 'Edit'

      return 'Show'
    },
    approveUrl(id) {
      this.isRequestSent = true
      axios.post(`${BASE_AJAX_URL}${id}/approve`).then(() => {
        toastr.success('Moment approved successfully')
        this.$emit('updateMomentList')
      }).catch((err) => {
        handleError(err)
      }).finally(() => {
        this.isRequestSent = false
      })
    },
    rejectUrl(id) {
      this.isRequestSent = true
      axios.post(`${BASE_AJAX_URL}${id}/reject`).then(() => {
        toastr.success('Moment rejected successfully')
        this.$emit('updateMomentList')
      }).catch((err) => {
        handleError(err)
      }).finally(() => {
        this.isRequestSent = false
      })
    },
    localPostUrl(checkin) {
      return checkin.localPostSearchUrl
    },
    destroyCheckin(id) {
      runSwal(() => {
        this.isRequestSent = true
        axios.delete(`${BASE_AJAX_URL}${id}`).then((res) => {
          toastr.success(res.data.message)
          this.$emit('updateMomentList')
        }).catch(() => {
          toastr.error(DEFAULT_ERROR_MESSAGE)
        }).finally(() => {
          this.isRequestSent = false
        })
      })
    },
    openPreview(media, index) {
      this.$emit('openPreview', {
        mediaSet: media,
        mediaIndex: index
      })
    },
    webhookStatusDescription(status, deliveredAt) {
      if (status === 'skipped') return 'skipped'
      if (deliveredAt) return 'delivered'

      return 'pending'
    },
    gmbPostStatus(checkin) {
      if (checkin.localPostStatus) return checkin.localPostStatus
      if (checkin.business.disconnected) return 'disconnected'
      if (checkin.postingError) return 'failed'

      return 'pending'
    },
    facebookStatus(status) {
      return FB_STATUSES[status]
    },
    videoStateIcon(state) {
      if (state === 'live') {
        return 'far fa-check-circle font-size-16 color-green'
      }

      return 'far fa-times-circle font-size-16 text-grey'
    },
    facebookPostPublished(checkin) {
      return checkin.facebookPostingStatus === 'published' && checkin.facebookPostUrl
    },
    instagramPostPublished(checkin) {
      return checkin.instagramPostingStatus === 'published' && checkin.instagramPostUrl
    },
    facebookPostPresent(checkin) {
      return checkin.facebookPostingStatus !== 'skipped'
    },
    instagramPostPresent(checkin) {
      return checkin.instagramPostingStatus !== 'skipped'
    }
  }
}
</script>
